import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

type Translator = Pick<TranslateService, 'instant'>;

function extractString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function isGenericBackendMessage(message: string): boolean {
  const normalized = message.trim().toLowerCase();
  return normalized === 'validation failed' || normalized === 'validation error' || normalized === 'bad request';
}

function fallbackForStatus(status: number, fallback: string, translate: Translator): string {
  if (status === 0) {
    return translate.instant('feedbackWidget.errors.network');
  }

  if (status === 408) {
    return translate.instant('feedbackWidget.errors.timeout');
  }

  if (status === 413) {
    return translate.instant('feedbackWidget.errors.payloadTooLarge');
  }

  if (status >= 500) {
    return translate.instant('feedbackWidget.errors.server');
  }

  return fallback;
}

export function extractFeedbackErrorMessage(
  error: unknown,
  translate: Translator,
  fallback: string = translate.instant('feedbackWidget.errors.requestFailed'),
): string {
  if (error instanceof HttpErrorResponse) {
    const body = error.error;

    if (typeof body === 'string' && body.trim()) {
      return body.trim();
    }

    if (body && typeof body === 'object') {
      const record = body as Record<string, unknown>;
      const candidate = extractString(record['message']) ?? extractString(record['detail']);

      const fieldErrors = record['fieldErrors'];
      if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
        const messages = fieldErrors
          .map((fieldError) => {
            if (!fieldError || typeof fieldError !== 'object') {
              return null;
            }

            const errorRecord = fieldError as Record<string, unknown>;
            return (
              extractString(errorRecord['errorMessage']) ??
              extractString(errorRecord['message']) ??
              extractString(errorRecord['detail'])
            );
          })
          .filter((message): message is string => Boolean(message));

        if (messages.length) {
          if (candidate && !isGenericBackendMessage(candidate)) {
            return `${candidate}: ${messages.join(' ')}`;
          }

          return messages.join(' ');
        }
      }

      if (candidate) {
        return candidate;
      }
    }

    return extractString(error.message) ?? fallbackForStatus(error.status, fallback, translate);
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }

  return fallback;
}
